/**
 * ワールドのデータの型。gen が作り、sim と render が読み取り専用で共有する。
 * 詳細は docs/03_simulation.md 2 章と docs/04_world-and-weather.md。
 */
import type { Vec2, Vec3 } from './vec';

export const Biome = {
  Lake: 0,
  Road: 1,
  Meadow: 2,
  Forest: 3,
  Wetland: 4,
  Scree: 5,
  Rock: 6,
  Snow: 7,
  /** 岩壁：歩いては通れない（コース設計の障害。docs/01 8 章） */
  Cliff: 8,
} as const;
export type Biome = (typeof Biome)[keyof typeof Biome];
export const BIOME_COUNT = 9;

export const BIOME_LABEL_JA: Record<Biome, string> = {
  [Biome.Lake]: '湖',
  [Biome.Road]: '道',
  [Biome.Meadow]: '草原',
  [Biome.Forest]: '森',
  [Biome.Wetland]: '湿地',
  [Biome.Scree]: 'ガレ場',
  [Biome.Rock]: '岩稜',
  [Biome.Snow]: '雪',
  [Biome.Cliff]: '岩壁',
};

export type NodeKind =
  'start' | 'checkpoint' | 'goal' | 'valley' | 'pass' | 'launchLow' | 'launchRidge' | 'launchPeak' | 'temp';

export const NODE_KIND_LABEL_JA: Record<NodeKind, string> = {
  start: 'スタート',
  checkpoint: 'チェックポイント',
  goal: 'ゴール',
  valley: '谷',
  pass: '峠',
  launchLow: '低い離陸地',
  launchRidge: '稜線の離陸地',
  launchPeak: '山頂',
  temp: '臨時地点',
};

export type LaunchLevel = 'low' | 'ridge' | 'peak';

export interface NodeData {
  id: number;
  kind: NodeKind;
  name?: string;
  pos: Vec3;
  /**
   * 離陸地の情報。facingRad は代表の向き（斜面の下り方向）、facings は滑空の余裕がある向きの一覧。
   * 離陸時は、facings のうち風上に最も近い向きを使う。
   */
  launch?: { level: LaunchLevel; facingRad: number; facings: number[] };
  massifId?: number;
  /** checkpoint / goal の順番（1 始まり） */
  cpIndex?: number;
}

export interface EdgeSegment {
  lengthM: number;
  /** 高低差 / 水平距離（a → b の向き） */
  grade: number;
  biome: Biome;
}

export interface EdgeData {
  id: number;
  a: number;
  b: number;
  lengthM: number;
  /** a → b の向きの登り・下り */
  ascentM: number;
  descentM: number;
  segments: EdgeSegment[];
  /** 描画用の座標列（x, y, z の繰り返し） */
  polyline: Float32Array;
}

export interface LakeData {
  center: Vec2;
  radiusX: number;
  radiusZ: number;
  levelM: number;
}

export interface CourseData {
  /** スタート、CP1..n、ゴールのノード ID */
  nodeIds: number[];
}

export interface GenStats {
  timingsMs: Record<string, number>;
  nodeCounts: Partial<Record<NodeKind, number>>;
  edgeCount: number;
  /** 検証の結果。ok=false は致命的ではないが要確認 */
  checks: { name: string; ok: boolean; detail: string }[];
}

export interface WorldData {
  seed: number;
  widthM: number;
  depthM: number;
  /** 最終解像度の格子 */
  nx: number;
  nz: number;
  cellM: number;
  height: Float32Array;
  biome: Uint8Array;
  nodes: NodeData[];
  edges: EdgeData[];
  course: CourseData;
  lakes: LakeData[];
  stats: GenStats;
}
